import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, Share2, CheckCheck, AlertTriangle, RefreshCw, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { listTime } from "@/components/whatsapp/chatUtils";
import { alertsEnabled, beep } from "@/components/whatsapp/WaAlerts";

const KIND = {
  wa_handoff: { icon: MessageCircle, cls: "text-emerald-500 bg-emerald-500/10", label: "واتساب" },
  social_published: { icon: Share2, cls: "text-sky-500 bg-sky-500/10", label: "قنوات التواصل" },
  social_failed: { icon: AlertTriangle, cls: "text-red-500 bg-red-500/10", label: "قنوات التواصل" },
  social_reauth: { icon: RefreshCw, cls: "text-amber-500 bg-amber-500/10", label: "قنوات التواصل" },
  ops_failure: { icon: AlertTriangle, cls: "text-red-600 bg-red-600/10", label: "عطل تشغيلي" },
};

const Item = ({ n, onOpen }) => {
  const k = n.kind === "wa_handoff" && /بريد|إيميل/.test(n.title) ? { icon: Mail, cls: "text-sky-500 bg-sky-500/10", label: "إيميل" } : (KIND[n.kind] || KIND.social_published);
  const Icon = k.icon;
  return (
    <button onClick={() => onOpen(n)} className={`w-full text-start flex gap-3 px-3 py-2.5 hover:bg-secondary/70 transition-colors ${n.read ? "" : "bg-primary/5"}`} data-testid={`notif-item-${n.id}`} dir="auto">
      <span className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${k.cls}`}><Icon className="w-4 h-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2"><span className={`text-sm truncate flex-1 ${n.read ? "font-medium" : "font-extrabold"}`}>{n.title}</span>{!n.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}</span>
        {n.body && <span className="block text-xs text-muted-foreground line-clamp-2">{n.body}</span>}
        <span className="block text-[10px] text-muted-foreground mt-0.5">{k.label} · {listTime(n.created_at)}</span>
      </span>
    </button>
  );
};

// جرس التنبيهات: تصعيدات واتساب وأحداث قنوات التواصل — تُسجَّل بالتزامن مع البريد
export const NotificationBell = ({ className = "" }) => {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const prevUnread = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = (await api.get("/notifications", { params: { limit: 30 } })).data;
      setData(d);
      if (prevUnread.current !== null && d.unread > prevUnread.current) {
        const latest = d.items.find((n) => !n.read);
        if (alertsEnabled()) beep();
        if (latest && latest.kind !== "wa_handoff") toast(latest.title, { description: latest.body, duration: 8000 });
      }
      prevUnread.current = d.unread;
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 25000); return () => clearInterval(t); }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) { api.post("/notifications/read", { ids: [n.id] }).then(load).catch(() => {}); }
    if (n.project_id) localStorage.setItem("rawaj_active", n.project_id);
    if (n.link?.startsWith("http")) window.open(n.link, "_blank", "noopener"); else if (n.link) nav(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read", {}).catch(() => {}); load(); };
  const clear = async () => { await api.post("/notifications/clear").catch(() => {}); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`relative w-10 h-10 rounded-xl border border-border grid place-items-center hover:bg-secondary transition-colors ${className}`} aria-label="التنبيهات" title="التنبيهات" data-testid="notif-bell">
          <Bell className={`w-4 h-4 ${data.unread ? "text-primary" : ""}`} />
          {data.unread > 0 && <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center px-1 animate-in zoom-in" data-testid="notif-badge">{data.unread > 99 ? "99+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] max-w-[92vw] p-0 overflow-hidden" dir="rtl" data-testid="notif-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="font-extrabold text-sm flex items-center gap-1.5"><Bell className="w-4 h-4 text-primary" /> التنبيهات {data.unread > 0 && <span className="text-xs text-muted-foreground font-normal">({data.unread} جديد)</span>}</div>
          <div className="flex items-center gap-1">
            {data.unread > 0 && <button onClick={readAll} className="h-7 rounded-full px-2 text-[11px] font-bold text-primary hover:bg-primary/10 flex items-center gap-1" data-testid="notif-read-all"><CheckCheck className="w-3.5 h-3.5" /> قرأت الكل</button>}
            {data.items.length > 0 && <button onClick={clear} className="h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary" title="مسح القائمة" data-testid="notif-clear"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-border/60">
          {data.items.length === 0 ? <div className="py-10 text-center text-xs text-muted-foreground" data-testid="notif-empty">لا تنبيهات بعد — ستصلك هنا تصعيدات واتساب وأحداث قنوات التواصل بالتزامن مع البريد</div>
            : data.items.map((n) => <Item key={n.id} n={n} onOpen={openItem} />)}
        </div>
      </PopoverContent>
    </Popover>
  );
};
